// Central runtime configuration for the display app.
//
// A physical screen is "paired" by opening its display URL once:
//
//   https://<display-host>/screen/<screenId>?key=<deviceKey>
//
// (the legacy form  /?screenId=<id>&screenKey=<key>  also works). The pairing
// is stored in localStorage and the key is scrubbed from the address bar, so
// every later load of the plain origin keeps showing that screen. Build-time
// env vars (REACT_APP_SCREEN_ID / REACT_APP_SCREEN_KEY) act as a fallback for
// per-screen builds. Secrets must never be hard-coded in source.

const STORAGE_ID_KEY = 'ww_screen_id';
const STORAGE_KEY_KEY = 'ww_screen_key';

// Where the backend API lives. Empty string = same origin (prod) or CRA proxy (dev).
export const API_BASE = process.env.REACT_APP_API_BASE || '';

// Mapbox access token for the intro map. Comes from frontend/.env.
export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

function readUrlPairing() {
  const pathMatch = window.location.pathname.match(/^\/screen\/([^/]+)\/?$/);
  const params = new URLSearchParams(window.location.search);
  return {
    id: pathMatch ? decodeURIComponent(pathMatch[1]) : params.get('screenId'),
    key: params.get('key') || params.get('screenKey')
  };
}

// Precedence: pairing URL > stored pairing > build-time env.
function resolveCredentials() {
  if (typeof window === 'undefined') {
    return {
      id: process.env.REACT_APP_SCREEN_ID || '',
      key: process.env.REACT_APP_SCREEN_KEY || '',
      source: 'env'
    };
  }

  const fromUrl = readUrlPairing();
  if (fromUrl.id && fromUrl.key) {
    window.localStorage.setItem(STORAGE_ID_KEY, fromUrl.id);
    window.localStorage.setItem(STORAGE_KEY_KEY, fromUrl.key);
    // Don't leave the device key sitting in the address bar / browser history;
    // the stored pairing keeps working across reloads.
    window.history.replaceState(null, '', '/');
    return { id: fromUrl.id, key: fromUrl.key, source: 'url' };
  }

  const storedId = window.localStorage.getItem(STORAGE_ID_KEY);
  const storedKey = window.localStorage.getItem(STORAGE_KEY_KEY);
  if (storedId && storedKey) {
    return { id: storedId, key: storedKey, source: 'storage' };
  }

  return {
    id: process.env.REACT_APP_SCREEN_ID || '',
    key: process.env.REACT_APP_SCREEN_KEY || '',
    source: 'env'
  };
}

const credentials = resolveCredentials();

// This physical screen's identity, used to fetch its own schedule.
export const SCREEN_ID = credentials.id;
export const SCREEN_KEY = credentials.key;

export function hasScreenCredentials() {
  return Boolean(SCREEN_ID && SCREEN_KEY);
}

// True when the active credentials came from a runtime pairing (URL/localStorage)
// rather than the build-time env vars.
export function pairingIsFromStorage() {
  return credentials.source !== 'env';
}

// Drops a stale runtime pairing so the next load falls back to the env values
// (or the not-paired screen). Used when the backend rejects the current
// credentials, e.g. after a device-key rotation.
export function clearStoredPairing() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_ID_KEY);
  window.localStorage.removeItem(STORAGE_KEY_KEY);
}
